import { useState } from 'react'
import { Badge, CloseButton, Col, ListGroup, Row, Stack } from 'react-bootstrap'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { ConfirmationModal } from '../../common'
import { ArticleType, type ArticleTypeValue } from '../../common/types'
import { closeModal, openModal, selectCell } from '../articleActions'
import { deleteFigure } from '../articleAPI'
import type { FormConfig } from '../../common/form/CustomForm'
import type { Figure } from '../types'

const figureListItemClassName = 'bg-light py-1 px-2'

type Dispatch = ThunkDispatch<unknown, unknown, UnknownAction>

type AssignedFigure = Figure & {
  id: string | number
  type: ArticleTypeValue
  articleId: number
  rowId: string | number
}

type UnassignedFigure = Figure & {
  id: string | number
}

export const AssignedFiguresList = ({
  dispatch,
  assignedFigures
}: {
  dispatch: Dispatch
  assignedFigures?: AssignedFigure[] | null
}) => {
  if (!assignedFigures?.length) {
    return
  }
  return (
    <Row className='align-items-center assignedFiguresList my-2'>
      <Col>
        <Stack direction='horizontal' gap={4}>
          <div className='fs-5'>割り当て済み写真・図</div>
          <div className='assignedFigures'>
            <ListGroup horizontal>
              {assignedFigures?.map((f) => {
                const handleClick = (e: React.MouseEvent) => {
                  e.preventDefault()
                  const { articleId, type, rowId } = f
                  const column = type === ArticleType.Template ? 'templateArticle' : 'figures'
                  dispatch(selectCell({ articleId, rowId, column }))
                }
                return (
                  <ListGroup.Item
                    key={`assigned-figure-${f.id}`}
                    action
                    className={figureListItemClassName}
                    onClick={handleClick}
                  >
                    <div className='d-flex align-items-center'>
                      {f.type === ArticleType.Template && (
                        <Badge pill bg='secondary'>
                          在版
                        </Badge>
                      )}
                      {f.filename}
                    </div>
                  </ListGroup.Item>
                )
              })}
            </ListGroup>
          </div>
        </Stack>
      </Col>
    </Row>
  )
}

export const UnassignedFiguresList = ({
  dispatch,
  openedModal,
  layoutableId,
  layoutableType,
  unassignedFigures
}: {
  dispatch: Dispatch
  openedModal: string | null
  layoutableId?: string | number
  layoutableType: string
  unassignedFigures?: UnassignedFigure[] | null
}) => {
  const [selectedFigureId, setSelectedFigureId] = useState<string | number | undefined>(undefined)
  if (!unassignedFigures?.length) {
    return
  }
  const handleSubmit = ({ figureId }: { figureId: string | number | undefined }) => {
    const delFigure = unassignedFigures?.find((f) => f.id === figureId)
    if (!delFigure) {
      return
    }
    console.log('will delete this figure!', delFigure)
    dispatch(
      deleteFigure({
        layoutableType,
        layoutableId,
        figure: delFigure,
        showAlert: true
      })
    )
  }
  const modalSetting = {
    name: 'delete-unassigned-figure',
    form: {
      initialValues: {
        figureId: selectedFigureId
      },
      handleSubmit,
      props: {
        title: '未割り当て写真・図削除',
        submitButtonText: '削除する',
        onClickCancel: () => dispatch(closeModal()),
        ConfirmationBody: ({ selectedFigureId }: { selectedFigureId: string | number | undefined }) => {
          const fig = unassignedFigures?.find((f) => f.id === selectedFigureId)
          if (!fig) {
            return
          }
          return <>「{fig.filename}」を削除しますか？</>
        },
        selectedFigureId
      }
    } as unknown as FormConfig
  }
  return (
    <Row className='align-items-center unassignedFiguresList my-2'>
      <Col>
        <Stack direction='horizontal' gap={4}>
          <ConfirmationModal
            key='delete-unassigned-figure-modal'
            name={modalSetting.name}
            form={modalSetting.form}
            openedModal={openedModal}
          />
          <div className='fs-5'>未割り当て写真・図</div>
          <div className='unassignedFigures'>
            <ListGroup horizontal>
              {unassignedFigures?.map((f) => {
                const figId = f.id
                const handleClick = (e: React.MouseEvent) => {
                  e.preventDefault()
                  setSelectedFigureId(figId)
                  dispatch(openModal('delete-unassigned-figure'))
                }
                return (
                  <ListGroup.Item key={`unassigned-figure-${f.id}`} className={figureListItemClassName}>
                    <div className='d-flex align-items-center'>
                      <CloseButton onClick={handleClick} />
                      {f.filename}
                    </div>
                  </ListGroup.Item>
                )
              })}
            </ListGroup>
          </div>
        </Stack>
      </Col>
    </Row>
  )
}
